import { useAuth } from "@clerk/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ClientOnly } from "@tanstack/react-router";
import { ImageOffIcon } from "lucide-react";
import { useCallback, useState, type CSSProperties } from "react";
import { useSpacetimeDB } from "spacetimedb/tanstack";

import { useUser } from "@/web/auth/useUser";
import { toaster } from "@/web/components/primitives/toast";
import {
  canSubmitPledge,
  EMPTY_PLEDGE_FORM_STATE,
  PledgeDialog,
} from "@/web/components/styled/PledgeForm";
import {
  businessCardPledgeSeed,
  hadExistingPledge,
  isPledgeInterestLoading,
  pledgeFormSubmitFields,
  useMyBusinessCardInterest,
  usePledgeDialogForm,
} from "@/web/components/styled/pledgeFormState";
import { env } from "@/web/lib/env";
import { ErrorHandler } from "@/web/lib/errors";
import {
  type BusinessCategoryType,
  getFriendlyName,
} from "@/web/lib/schemaUtils/businessCategories";
import { cn } from "@/web/lib/styling";
import { formatUSDShort } from "@/web/lib/formatting";
import { tableQueryOptions, useProcedureMutation } from "@/web/lib/tanstack";
import type { Uuid } from "spacetimedb";

import type { DbConnection } from "@tycho/web-bindings";
import { procedures, tables } from "@tycho/web-bindings";
import type { ShareableBusinessCardRecipientRow } from "@tycho/web-bindings/types";

// ===== presentational types =====

/**
 * Visual subset of the recipient row needed to render the card chrome (badges,
 * tagline, hero, gradient, brand chip). Pulled out of
 * `ShareableBusinessCardRecipientRow` so the producer-side preview drawer can
 * map a `ShareBusinessCardContextRow` into the same shape without dragging
 * recipient-only fields (EOI count, the calling user's existing EOI) along.
 */
export type TtwCardPresentation = Pick<
  ShareableBusinessCardRecipientRow,
  | "brandFriendlyName"
  | "brandHeroCdnPath"
  | "brandColorPrimary"
  | "brandBusinessType"
  | "brandBusinessAddress"
  | "targetedPropertyAddress"
>;

type TtwCardCommonProps = {
  /**
   * Override the outer wrapper className — typically the max-width of the
   * card. The default ships with `max-w-[393px]`; the producer-side preview
   * drawer overrides this to fit the card alongside the form controls.
   */
  className?: string;
};

type TtwCardLiveProps = {
  preview?: false;
  presentation: TtwCardPresentation;
  /**
   * Full recipient row — required so the pledge section can read the calling
   * user's existing pledge amount and the total count without a second
   * round-trip.
   */
  card: ShareableBusinessCardRecipientRow;
  cardId: Uuid;
  accessCode: string;
  /** Re-fetch query key matched to the route's `useQuery`. */
  refetchKey: ReadonlyArray<unknown>;
};

type TtwCardPreviewProps = {
  /**
   * Renders a non-interactive preview of the card. The "Pledge" button is
   * rendered as a static `<div>` — no auth dialog, no mutation, no tooltip,
   * no focus management. Used in the SMB-side share drawer so the SMB can see
   * what recipients will see before they hit Share.
   */
  preview: true;
  presentation: TtwCardPresentation;
  /**
   * Live recipient row (the same shape `/ttw/<id>` reads via
   * `getShareableBusinessCard`). When provided, the count line under the
   * static CTA reflects the row's `expressionsOfInterestCount` instead of
   * the static "Be the first to pledge." placeholder, so the preview mirrors
   * the real page even after pledges start landing.
   *
   * The row's `user*` fields (which carry the *calling user's* own pledge)
   * are intentionally ignored: the preview is always rendered as if for an
   * anonymous visitor, never the SMB themself — otherwise a card the SMB
   * has already pledged on would preview the "Pledged: $X" submitted state,
   * which isn't what a recipient will see.
   */
  recipientRow?: ShareableBusinessCardRecipientRow;
};

export type TtwCardProps = TtwCardCommonProps & (TtwCardLiveProps | TtwCardPreviewProps);

// ===== presentational helpers =====

/** Fallback gradient color when the brand has no `colorPrimary`. Mirrors the
 * producer-side `ShareBusinessCardAction` so the recipient & sender previews
 * agree on the wash treatment when no brand color is set. */
const FALLBACK_GRADIENT_COLOR = "#3c524f";

/**
 * Builds an `image: linear-gradient(...)` value tied to a brand color so the
 * card's top wash matches the brand's primary color. Same convention as the
 * producer-side `ShareBusinessCardAction` — see comment there for why we
 * don't use Tailwind's `from-[#xxx]/95` for dynamic colors.
 */
function brandGradientStyle(colorHex: string | undefined): CSSProperties {
  const color = (colorHex ?? FALLBACK_GRADIENT_COLOR).trim();
  const isSixHex = /^#[0-9a-f]{6}$/i.test(color);
  if (isSixHex) {
    return {
      backgroundImage: `linear-gradient(to bottom, ${color}f2 0%, ${color}8c 50%, ${color}00 100%)`,
    };
  }
  return {
    backgroundImage: `linear-gradient(to bottom, color-mix(in srgb, ${color} 95%, transparent) 0%, color-mix(in srgb, ${color} 55%, transparent) 50%, transparent 100%)`,
  };
}

/**
 * Bottom-up wash that brings the hero image down toward the CTA strip; mirrors
 * the figma's lower gradient cover so the white "Pledge" button sits legibly
 * over an arbitrary hero photo.
 */
function bottomGradientStyle(): CSSProperties {
  return {
    backgroundImage: `linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.35) 35%, rgba(0,0,0,0) 70%)`,
  };
}

function HeroPlaceholder() {
  return (
    <div
      aria-hidden
      className="absolute inset-0 z-0 flex items-center justify-center bg-muted text-muted-foreground"
    >
      <ImageOffIcon className="size-12" />
    </div>
  );
}

function initialsFromName(name: string | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0]?.[0] ?? "?";
  const second = parts[1]?.[0] ?? parts[0]?.[1] ?? "";
  return `${first}${second}`.toUpperCase();
}

// ===== component =====

export function TtwCard(props: TtwCardProps) {
  const { presentation, className } = props;
  const businessName = presentation.brandFriendlyName ?? "This business";
  const categoryLabel = getFriendlyName(
    presentation.brandBusinessType as BusinessCategoryType | undefined,
  );
  const locationAddress = presentation.targetedPropertyAddress ?? presentation.brandBusinessAddress;
  const locationLabel = locationAddress
    ? [locationAddress.city, locationAddress.state].filter(Boolean).join(", ")
    : "";
  const taglineHighlight = presentation.targetedPropertyAddress?.addressLine1?.trim();
  const initials = initialsFromName(presentation.brandFriendlyName);

  const heroImageSrc = presentation.brandHeroCdnPath
    ? `${env.VITE_WITHCO_IMAGE_CDN_BASE}/${presentation.brandHeroCdnPath}`
    : undefined;

  const heroSurfaceStyle: CSSProperties | undefined = heroImageSrc
    ? {
        backgroundImage: `url(${heroImageSrc})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }
    : undefined;

  const gradientStyle = brandGradientStyle(presentation.brandColorPrimary);
  const bottomShade = bottomGradientStyle();

  return (
    <article
      // 7:10 ≈ 393:560, the figma frame's aspect ratio. The fallback
      // `bg-muted` paints the outer wrapper so a missing/broken hero
      // degrades to gray instead of a see-through hole.
      className={cn(
        "relative flex aspect-7/10 w-full max-w-[393px] flex-col overflow-hidden bg-muted shadow-withus-default",
        className,
      )}
      style={heroSurfaceStyle}
      aria-label={`${businessName} business card`}
    >
      {heroImageSrc ? null : <HeroPlaceholder />}
      <div aria-hidden className="pointer-events-none absolute inset-0 z-0" style={gradientStyle} />
      <div aria-hidden className="pointer-events-none absolute inset-0 z-0" style={bottomShade} />

      <div className="relative z-10 flex flex-col gap-5 p-6 text-withus-white">
        <div className="flex flex-wrap gap-2">
          {categoryLabel ? (
            <span className="border border-white/90 bg-black/15 px-2 py-2 text-[11px] leading-none font-semibold tracking-wider uppercase backdrop-blur-[2px]">
              {categoryLabel}
            </span>
          ) : null}
          {locationLabel ? (
            <span className="border border-white/90 bg-black/15 px-2 py-2 text-[11px] leading-none font-semibold tracking-wider uppercase backdrop-blur-[2px]">
              {locationLabel}
            </span>
          ) : null}
        </div>
        <p className="withus-strong text-[24px] leading-[28px]">
          Own{" "}
          {taglineHighlight ? (
            <span className="underline decoration-white/50 decoration-dotted underline-offset-4">
              {taglineHighlight}
            </span>
          ) : (
            "a building"
          )}{" "}
          with us
        </p>
        <div className="flex items-center gap-2">
          <div
            aria-hidden
            className="flex size-7 shrink-0 items-center justify-center rounded-full bg-white text-[10px] font-semibold tracking-wide text-foreground uppercase"
          >
            {initials}
          </div>
          <p className="min-w-0 text-sm leading-snug font-semibold text-white">{businessName}</p>
        </div>
      </div>

      <div className="relative z-10 mt-auto p-4">
        {props.preview ? (
          <PreviewCta recipientRow={props.recipientRow} />
        ) : (
          // PledgeSection calls useSpacetimeDB / Clerk hooks that have no provider
          // server-side (ClientProviders is mounted under ClientOnly in __root).
          // The CTA + count skeleton paints during SSR and matches the live
          // section's first render so hydration is layout-stable.
          <ClientOnly fallback={<PledgeSkeleton card={props.card} />}>
            <PledgeSection
              cardId={props.cardId}
              accessCode={props.accessCode}
              refetchKey={props.refetchKey}
              card={props.card}
              businessName={businessName}
            />
          </ClientOnly>
        )}
      </div>
    </article>
  );
}

// ===== preview CTA =====

/**
 * Static, non-interactive twin of the live "Pledge" button. Renders as a
 * `<div>` so the SMB-side preview can't accidentally trigger any of the pledge
 * flow (auth dialog, mutation, focus management).
 */
function PreviewCta({
  recipientRow,
}: {
  /**
   * Live anonymous recipient row from `getShareableBusinessCard`. We only
   * read `expressionsOfInterestCount` here — the row's `user*` fields carry
   * the *calling* user's own pledge and would incorrectly flip the preview
   * into the "You've pledged" state when the SMB previews a card they've
   * already pledged on. The preview is always a visitor view.
   */
  recipientRow?: ShareableBusinessCardRecipientRow;
}) {
  // Match the live `<PledgeSection />` count line so the preview is
  // pixel-identical to the page below the fold once a pledge exists. `0`
  // (no row yet, or genuinely zero pledges) keeps the original "Be the
  // first to pledge." copy.
  const totalCount = recipientRow ? Number(recipientRow.expressionsOfInterestCount) : 0;
  const countLabel =
    totalCount === 0
      ? "Be the first to pledge."
      : `${totalCount} ${totalCount === 1 ? "person has" : "people have"} pledged.`;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex h-[42px] w-full items-center justify-center bg-withus-white px-4 py-3 text-sm font-semibold text-withus-dark-blue">
        Pledge
      </div>
      <p className="text-center text-xs text-white/70">{countLabel}</p>
    </div>
  );
}

// ===== pledge skeleton (SSR fallback) =====
//
// Renders the same visual frame as the not-yet-submitted PledgeSection branch:
// a static "Pledge" button and the pledge count line. No SDK or auth hooks
// fire — safe to run server-side. Hydration swaps in the live section.

function PledgeSkeleton({ card }: { card: ShareableBusinessCardRecipientRow }) {
  const totalCount = Number(card.expressionsOfInterestCount);
  const countLabel =
    totalCount === 0
      ? "Be the first to pledge."
      : `${totalCount} ${totalCount === 1 ? "person has" : "people have"} pledged.`;
  return (
    <div className="flex flex-col gap-2">
      <div
        className={cn(
          "flex h-[42px] w-full items-center justify-center bg-withus-white px-4 py-3",
          "text-sm font-semibold text-withus-dark-blue opacity-60",
        )}
      >
        Pledge
      </div>
      <p className="text-center text-xs text-white/70">{countLabel}</p>
    </div>
  );
}

// ===== pledge section (live mode only) =====

type PledgeSectionProps = {
  cardId: Uuid;
  accessCode: string;
  refetchKey: ReadonlyArray<unknown>;
  card: ShareableBusinessCardRecipientRow;
  /** Brand name for dialog copy ("Tell X how much you'd like to pledge..."). */
  businessName: string;
};

function PledgeSection({ cardId, accessCode, refetchKey, card, businessName }: PledgeSectionProps) {
  // oxlint-disable-next-line unbound-method - spacetime booboo
  const { getConnection } = useSpacetimeDB();
  const queryClient = useQueryClient();
  const { isLoaded, isSignedIn } = useAuth();
  const user = useUser();
  const { myInterest, isLoading: isMyInterestLoading } = useMyBusinessCardInterest(
    cardId.toString(),
  );

  const { seed, exactStoredNames } = businessCardPledgeSeed(myInterest, {
    amount:
      card.userInterestDollarAmount === undefined
        ? undefined
        : Number(card.userInterestDollarAmount),
    supportiveMessage: card.userSupportiveMessage,
    consentToDisplay: card.userConsentToDisplay,
  });
  const currentPledgeAmount = seed.amount;
  const hasMyPledge = currentPledgeAmount !== undefined;
  const totalCount = Number(card.expressionsOfInterestCount);

  const isPledgeStateLoading = isPledgeInterestLoading(isSignedIn ?? false, isMyInterestLoading);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [authPromptOpen, setAuthPromptOpen] = useState(false);
  const { formState, setFormState, initialFormStateRef, seedForm, clearForm, hasChanges } =
    usePledgeDialogForm();

  const openPledgeDialog = useCallback(() => {
    seedForm(seed, user, { exactStoredNames });
    setDialogOpen(true);
  }, [seed, exactStoredNames, seedForm, user]);

  const handleDialogOpenChange = useCallback(
    (open: boolean) => {
      setDialogOpen(open);
      if (!open) clearForm();
    },
    [clearForm],
  );

  const setPledge = useMutation({
    mutationFn: async (variables: {
      interestDollarAmount: bigint;
      firstName: string;
      lastName: string;
      email: string | undefined;
      supportiveMessage: string | undefined;
      consentToDisplay: boolean;
    }) => {
      const conn = getConnection() as DbConnection | null;
      if (!conn) throw new Error("Not connected to server.");
      await conn.procedures.setInterestInBusinessCard({
        cardId,
        accessCode,
        interestDollarAmount: variables.interestDollarAmount,
        firstName: variables.firstName || undefined,
        lastName: variables.lastName,
        // Anonymous pledges key on this email server-side; signed-in callers
        // pledge under their account email and the server ignores it.
        email: variables.email,
        supportiveMessage: variables.supportiveMessage || undefined,
        consentToDisplay: variables.consentToDisplay,
      });
    },
    onSuccess: async () => {
      toaster({
        message: hadExistingPledge(initialFormStateRef.current)
          ? "Your pledge has been updated."
          : "Thanks — we've recorded your pledge.",
        variant: "success",
      });
      setDialogOpen(false);
      setFormState(EMPTY_PLEDGE_FORM_STATE);
      initialFormStateRef.current = null;
      // Anonymous pledger: nudge them to create an account so the connect-time
      // back-association links this EOI to their new identity.
      if (!isSignedIn) setAuthPromptOpen(true);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: refetchKey }),
        queryClient.invalidateQueries({
          queryKey: tableQueryOptions(tables.myBusinessCardInterests).queryKey,
        }),
      ]);
    },
    onError: ErrorHandler.default,
  });

  const removePledge = useProcedureMutation(procedures.removeInterestInBusinessCard, {
    onSuccess: async () => {
      toaster({ message: "Your pledge has been removed.", variant: "success" });
      setDialogOpen(false);
      setFormState(EMPTY_PLEDGE_FORM_STATE);
      initialFormStateRef.current = null;
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: refetchKey }),
        queryClient.invalidateQueries({
          queryKey: tableQueryOptions(tables.myBusinessCardInterests).queryKey,
        }),
      ]);
    },
    onError: ErrorHandler.default,
  });

  const onPrimaryClick = useCallback(() => {
    if (!isLoaded || isPledgeStateLoading) return;
    openPledgeDialog();
  }, [isLoaded, isPledgeStateLoading, openPledgeDialog]);

  const onSubmit = useCallback(() => {
    if (formState.amount === undefined) return;
    if (!canSubmitPledge(formState, isSignedIn ?? false)) return;

    const fields = pledgeFormSubmitFields(formState, isSignedIn ?? false);
    void setPledge.mutateAsync({
      interestDollarAmount: BigInt(formState.amount),
      firstName: fields.firstName ?? "",
      lastName: fields.lastName ?? "",
      email: fields.email,
      supportiveMessage: fields.supportiveMessage,
      consentToDisplay: fields.consentToDisplay,
    });
  }, [formState, isSignedIn, setPledge]);

  const canSubmit = hasChanges && canSubmitPledge(formState, isSignedIn ?? false);

  // ===== Submitted state — button shows amount and opens the edit dialog.
  if (hasMyPledge) {
    const myAmount = Number(currentPledgeAmount ?? 0);
    const others = Math.max(totalCount - 1, 0);
    const othersLabel =
      others === 0
        ? "You're the first person to pledge."
        : `You and ${others} other ${others === 1 ? "person have" : "people have"} pledged.`;
    return (
      <>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={onPrimaryClick}
            disabled={!isLoaded || isPledgeStateLoading}
            className={cn(
              "flex h-[42px] w-full items-center justify-center border border-solid border-white px-4 py-3",
              "text-sm font-semibold text-white transition-opacity",
              "disabled:cursor-not-allowed disabled:opacity-60",
            )}
          >
            <span className="font-normal">Pledged:&nbsp;</span>
            <span>{formatUSDShort(myAmount)}</span>
          </button>
          <p className="text-center text-xs text-white/70">{othersLabel}</p>
        </div>

        <PledgeDialog
          businessName={businessName}
          canSubmit={canSubmit}
          currentPledgeAmount={currentPledgeAmount}
          formState={formState}
          isPending={setPledge.isPending}
          isSignedIn={isSignedIn ?? false}
          onFormStateChange={setFormState}
          onOpenChange={handleDialogOpenChange}
          onSubmit={onSubmit}
          open={dialogOpen}
          authPromptOpen={authPromptOpen}
          onAuthPromptOpenChange={setAuthPromptOpen}
          onDelete={
            isSignedIn && hadExistingPledge(initialFormStateRef.current)
              ? () => void removePledge.mutateAsync({ cardId })
              : undefined
          }
          isDeleting={removePledge.isPending}
        />
      </>
    );
  }

  // ===== Not yet pledged — primary CTA + count.
  const countLabel =
    totalCount === 0
      ? "Be the first to pledge."
      : `${totalCount} ${totalCount === 1 ? "person has" : "people have"} pledged.`;

  return (
    <>
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={onPrimaryClick}
          disabled={!isLoaded || isPledgeStateLoading}
          className={cn(
            "flex h-[42px] w-full items-center justify-center bg-withus-white px-4 py-3",
            "text-sm font-semibold text-withus-dark-blue transition-opacity",
            "disabled:cursor-not-allowed disabled:opacity-60",
          )}
        >
          Pledge
        </button>

        <p className="text-center text-xs text-white/70">{countLabel}</p>
      </div>

      <PledgeDialog
        businessName={businessName}
        canSubmit={canSubmit}
        currentPledgeAmount={currentPledgeAmount}
        formState={formState}
        isPending={setPledge.isPending}
        isSignedIn={isSignedIn ?? false}
        onFormStateChange={setFormState}
        onOpenChange={handleDialogOpenChange}
        onSubmit={onSubmit}
        open={dialogOpen}
        authPromptOpen={authPromptOpen}
        onAuthPromptOpenChange={setAuthPromptOpen}
      />
    </>
  );
}
